import { type InputHTMLAttributes, useMemo } from 'react'
import classnames from 'classnames'
import { FormErrorsType } from '../../../../hooks/useForm/types'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { DEFAULT_AUTO_ANIMATE_SETTINGS } from '../../../../utils/constants'

export type FieldProps = Omit<
    InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
    'onChange'
> &
    FieldErrorsProps &
    FieldDescriptionProps & {
        name: string
        label?: string
        required?: boolean
        className?: string
    } & {
        onChange?: (value: string) => void
    }

export const Field: React.FC<FieldProps> = (props) => {
    const {
        children,
        className,
        description,
        disabled,
        errors,
        label,
        name,
        required,
    } = props

    const [parent] = useAutoAnimate(DEFAULT_AUTO_ANIMATE_SETTINGS)

    const hasErrors = useMemo<boolean>(
        () => !!errors && !!Object.keys(errors).length,
        [errors]
    )

    const wrapperClassnames = classnames(
        className,
        'relative flex gap-2 border items-center overflow-hidden py-2 px-3',
        'rounded-lg shadow-sm',
        'outline outline-0 focus-within:outline-4 outline-white/0 transition-all',
        disabled
            ? 'border-neutral-200'
            : hasErrors
              ? 'border-red-500'
              : 'border-neutral-300',
        disabled ? 'bg-neutral-50' : 'bg-white',
        disabled ? 'text-disabled' : 'text-regular',
        hasErrors
            ? 'focus-within:outline-red-500/25'
            : 'focus-within:border-azure focus-within:outline-azure/25'
    )

    return (
        <div className="space-y-1" ref={parent}>
            <label htmlFor={name} className="relative z-[200] block space-y-1">
                {label && <FieldLabel label={label} required={required} />}
                <div className={wrapperClassnames}>{children}</div>
            </label>
            {hasErrors && <FieldErrors name={name} errors={errors} />}
            <FieldDescription description={description} />
        </div>
    )
}

type FieldLabelProps = {
    label: string
    required?: boolean
}

export const FieldLabel: React.FC<FieldLabelProps> = (props) => {
    const { label, required } = props

    return (
        <div className="flex space-x-1">
            <span className="text-label-small text-subtle">{label}</span>
            {required && <span className="text-error">*</span>}
        </div>
    )
}

export type FieldErrorsProps = {
    errors?: FormErrorsType
    name: string
}

export const FieldErrors: React.FC<FieldErrorsProps> = (props) => {
    const { errors, name } = props

    if (!errors || Object.keys(errors).length === 0) {
        return null
    }

    return (
        <div
            data-testid={`${name}-errors`}
            className="text-body-medium space-y-1 text-sm text-red-500"
        >
            {Object.keys(errors).map((key) => (
                <div key={key} data-testid={`${name}-error-${key}`}>
                    {errors[key]}
                </div>
            ))}
        </div>
    )
}

export type FieldDescriptionProps = {
    description?: string
}

export const FieldDescription: React.FC<FieldDescriptionProps> = (props) => {
    const { description } = props

    if (!description) {
        return null
    }

    return <div className="text-gray-500 text-sm">{description}</div>
}
